import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card, Btn } from "../../components/UI";
import { FLabel, FInput, FSelect, FTextarea, SelectWithCustom } from "../engagement/FormControls";
import { DateField } from "../engagement/DateField";
import { SignModal } from "../../components/SignatureCanvas";
import { ClientSelect, useClients, SignatureDisplay } from "./shared";
import { MI_DIAGNOSES } from "../engagement/constants";
import { ArrowLeft, Lock, Plus, Trash2, PenLine, Link2 } from "lucide-react";
import ClinicalRecordLockControls from "../../components/ClinicalRecordLockControls";
import { toast } from "sonner";
import dayjs from "dayjs";
import { useSaveShortcut, SAVE_HINT } from "../../lib/useSaveShortcut";

const EMPTY_OBJECTIVE = { objective: "", interventions: "", frequency: "Weekly", status: "in_progress" };
const EMPTY_GOAL = { goal: "", target_date: "", objectives: [{ ...EMPTY_OBJECTIVE }] };
const EMPTY_PROBLEM = { problem: "", goals: [{ ...EMPTY_GOAL, objectives: [{ ...EMPTY_OBJECTIVE }] }] };
const EMPTY = { client_id: "", title: "Master Treatment Plan", diagnosis: "", start_date: dayjs().format("YYYY-MM-DD"), review_date: "", status: "draft", strengths: "", barriers: "", problems: [JSON.parse(JSON.stringify(EMPTY_PROBLEM))] };

function GoldenThreadPanel({ planId }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get(`/treatment-plans/${planId}/golden-thread`).then((r) => setData(r.data)).catch(() => {}); }, [planId]);
  if (!data || data.goals.length === 0) return null;
  return (
    <Card className="mt-5 p-6" data-testid="golden-thread-panel">
      <div className="mb-3 flex items-center gap-2">
        <div className="h-8 w-8 rounded-xl bg-[#3E2723] flex items-center justify-center text-[#FFB300]"><Link2 size={15} /></div>
        <h3 className="font-heading text-sm font-bold text-[#3E2723]">Golden Thread - progress notes addressing each goal</h3>
      </div>
      <div className="space-y-2">
        {data.goals.map((g) => (
          <div key={g.key} data-testid={`gt-goal-${g.key}`} className="rounded-xl border border-[#F1E8DA] bg-[#FDFCF8] p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-[#3E2723]">{g.goal || "(untitled goal)"}</div>
                {g.problem && <div className="truncate text-xs text-[#84675B]">{g.problem}</div>}
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold ${g.note_count > 0 ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#FFEBEE] text-[#C62828]"}`}>
                {g.note_count} {g.note_count === 1 ? "note" : "notes"}
              </span>
            </div>
            {g.notes.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {g.notes.map((n) => (
                  <button key={n.note_id} type="button" data-testid={`gt-note-${n.note_id}`} onClick={() => navigate(`/clinical/progress-notes/${n.note_id}`)}
                    className="rounded-full bg-[#F5F2EB] px-2.5 py-1 text-xs font-medium text-[#6D4C41] hover:bg-[#EDE7DC]">
                    {n.date} · {n.provider}
                  </button>
                ))}
              </div>
            )}
            {g.note_count === 0 && <p className="mt-1.5 text-xs text-[#C62828]">No documentation addresses this goal yet - golden thread gap.</p>}
          </div>
        ))}
      </div>
    </Card>
  );
}

const FREQUENCIES = ["Daily", "Weekly", "Bi-weekly", "Monthly", "As needed"];

export default function TreatmentPlanEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from || { url: "/clinical/treatment-plans", label: "Treatment Plans" };
  const goBack = () => navigate(returnTo.url);
  const { user } = useAuth();
  const clients = useClients();
  const [form, setForm] = useState(() => JSON.parse(JSON.stringify(EMPTY)));
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const preselect = searchParams.get("client");
    if (preselect && isNew) setForm((f) => ({ ...f, client_id: preselect }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signRole, setSignRole] = useState(null);

  const load = () => {
    if (isNew) return;
    api.get(`/treatment-plans/${id}`).then((r) => {
      // Backfill defaults so legacy plans that predate the problems/goals/objectives
      // schema still render without React crashing on undefined.map().
      const safe = {
        ...EMPTY,
        ...r.data,
        problems: Array.isArray(r.data?.problems) && r.data.problems.length
          ? r.data.problems.map((p) => ({
              ...EMPTY_PROBLEM,
              ...p,
              goals: Array.isArray(p?.goals) && p.goals.length
                ? p.goals.map((g) => ({
                    ...EMPTY_GOAL,
                    ...g,
                    objectives: Array.isArray(g?.objectives) && g.objectives.length
                      ? g.objectives.map((o) => ({ ...EMPTY_OBJECTIVE, ...o }))
                      : [{ ...EMPTY_OBJECTIVE }],
                  }))
                : [{ ...EMPTY_GOAL, objectives: [{ ...EMPTY_OBJECTIVE }] }],
            }))
          : [JSON.parse(JSON.stringify(EMPTY_PROBLEM))],
      };
      setPlan(r.data);
      setForm(safe);
    })
      .catch(() => { toast.error("Plan not found"); navigate("/clinical/treatment-plans"); });
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const locked = !!plan?.clinician_signature || !!plan?.locked;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const patch = (fn) => setForm((f) => { const c = JSON.parse(JSON.stringify(f)); fn(c); return c; });

  useSaveShortcut(() => { if (!busy) save(); }, !locked);
  const save = async () => {
    if (!form.client_id) { toast.error("Select a client"); return null; }
    setBusy(true);
    try {
      let r;
      if (isNew) r = await api.post("/treatment-plans", form);
      else r = await api.put(`/treatment-plans/${id}`, form);
      toast.success("Treatment plan saved");
      if (isNew) navigate(`/clinical/treatment-plans/${r.data.id}`, { replace: true });
      else { setPlan(r.data); setForm(r.data); }
      setBusy(false);
      return r.data;
    } catch (err) { toast.error(apiError(err)); setBusy(false); return null; }
  };

  const sign = async ({ name, image }) => {
    setBusy(true);
    try {
      if (!locked) await api.put(`/treatment-plans/${id}`, form);
      const r = await api.put(`/treatment-plans/${id}/sign`, { role: signRole, name, image });
      // Re-run the load() defaults pass so the returned plan is safe to render.
      const safe = { ...form, ...r.data,
        problems: Array.isArray(r.data?.problems) ? r.data.problems : form.problems };
      setPlan(r.data); setForm(safe); setSignRole(null);
      toast.success(`${signRole === "clinician" ? "Clinician" : "Client"} signature captured`);
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <div data-testid="treatment-plan-editor">
      <button data-testid="tp-back-btn" onClick={goBack} className="mb-3 flex items-center gap-1 text-sm text-[#7B6157] hover:underline">
        <ArrowLeft size={15} /> Back to {returnTo.label}
      </button>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-bold text-[#3E2723]">{isNew ? "New Treatment Plan" : `${form.title} - ${plan?.client_name || ""}`}</h1>
        <div className="flex flex-wrap items-center gap-2">
          {locked && !plan?.locked && <span data-testid="tp-locked-banner" className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F5E9] px-3.5 py-1.5 text-xs font-bold text-[#2E7D32]"><Lock size={12} /> Signed</span>}
          {!isNew && plan && (
            <ClinicalRecordLockControls kind="treatment_plan" record={plan} onChange={load}
              amendableFields={[
                { key: "presenting_problems", label: "Presenting Problems", type: "textarea", rows: 3 },
                { key: "review_date", label: "Review Date", type: "date" },
                { key: "target_date", label: "Target Date", type: "date" },
                { key: "notes", label: "Notes", type: "textarea", rows: 3 },
              ]} />
          )}
        </div>
      </div>

      <Card className="p-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
          <div><FLabel>Client</FLabel><ClientSelect clients={clients} value={form.client_id} onChange={(v) => setField("client_id", v)} testId="tp-client-select" disabled={!isNew || locked} /></div>
          <div><FLabel>Plan Title</FLabel><FInput data-testid="tp-title" value={form.title} onChange={(e) => setField("title", e.target.value)} disabled={locked} /></div>
          <div><FLabel>Primary Diagnosis</FLabel><SelectWithCustom testId="tp-diagnosis" value={form.diagnosis} onChange={(v) => setField("diagnosis", v)} options={MI_DIAGNOSES.filter((d) => d !== "Other")} customPlaceholder="Enter diagnosis" /></div>
          <div><FLabel>Start Date</FLabel><DateField testId="tp-start-date" value={form.start_date} onChange={(v) => setField("start_date", v)} /></div>
          <div><FLabel>Review Date</FLabel><DateField testId="tp-review-date" value={form.review_date} onChange={(v) => setField("review_date", v)} /></div>
          <div><FLabel>Status</FLabel>
            <FSelect data-testid="tp-status" value={form.status} onChange={(e) => setField("status", e.target.value)} disabled={locked}>
              <option value="draft">Draft</option><option value="active">Active</option><option value="completed">Completed</option><option value="discontinued">Discontinued</option>
            </FSelect>
          </div>
          <div className="sm:col-span-2 lg:col-span-3 grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div><FLabel>Client Strengths</FLabel><FTextarea data-testid="tp-strengths" rows={2} value={form.strengths} onChange={(e) => setField("strengths", e.target.value)} disabled={locked} /></div>
            <div><FLabel>Barriers to Treatment</FLabel><FTextarea data-testid="tp-barriers" rows={2} value={form.barriers} onChange={(e) => setField("barriers", e.target.value)} disabled={locked} /></div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm font-bold text-[#3E2723] uppercase tracking-wide">Problems, Goals & Objectives</h3>
            {!locked && (
              <Btn data-testid="tp-add-problem-btn" variant="outline" className="rounded-lg text-xs px-3 py-1.5"
                onClick={() => patch((c) => c.problems.push(JSON.parse(JSON.stringify(EMPTY_PROBLEM))))}><Plus size={13} /> Add Problem</Btn>
            )}
          </div>
          <div className="space-y-4">
            {(form.problems || []).map((prob, pi) => (
              <div key={pi} data-testid={`tp-problem-${pi}`} className="rounded-2xl border border-[#E5DFD3] bg-[#FDFCF8] p-4">
                <div className="flex items-start gap-3">
                  <span className="mt-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#4E342E] text-xs font-bold text-white">P{pi + 1}</span>
                  <div className="flex-1"><FLabel>Problem Statement</FLabel>
                    <FTextarea data-testid={`tp-problem-text-${pi}`} rows={2} value={prob.problem} onChange={(e) => patch((c) => { c.problems[pi].problem = e.target.value; })} disabled={locked} /></div>
                  {!locked && form.problems.length > 1 && (
                    <button type="button" data-testid={`tp-remove-problem-${pi}`} aria-label="Remove problem" onClick={() => patch((c) => c.problems.splice(pi, 1))}
                      className="mt-2 h-8 w-8 shrink-0 rounded-full bg-white border border-[#EFEBE0] text-[#7B6157] flex items-center justify-center hover:text-[#C62828]"><Trash2 size={13} /></button>
                  )}
                </div>

                <div className="mt-3 space-y-3 pl-10">
                  {(prob.goals || []).map((goal, gi) => (
                    <div key={gi} className="rounded-xl border border-[#EFEBE0] bg-white p-4">
                      <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px_auto] gap-3 items-end">
                        <div><FLabel>Goal {gi + 1}</FLabel><FInput data-testid={`tp-goal-${pi}-${gi}`} value={goal.goal} onChange={(e) => patch((c) => { c.problems[pi].goals[gi].goal = e.target.value; })} disabled={locked} /></div>
                        <div><FLabel>Target Date</FLabel><DateField testId={`tp-goal-date-${pi}-${gi}`} value={goal.target_date} onChange={(v) => patch((c) => { c.problems[pi].goals[gi].target_date = v; })} /></div>
                        {!locked && prob.goals.length > 1 && (
                          <button type="button" aria-label="Remove goal" onClick={() => patch((c) => c.problems[pi].goals.splice(gi, 1))}
                            className="h-9 w-9 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:text-[#C62828]"><Trash2 size={13} /></button>
                        )}
                      </div>
                      <div className="mt-3 space-y-2">
                        {(goal.objectives || []).map((obj, oi) => (
                          <div key={oi} className="rounded-lg bg-[#F9F7F1] p-3 grid grid-cols-1 lg:grid-cols-[1fr_1fr_150px_auto] gap-3 items-end">
                            <div><FLabel>Objective</FLabel><FInput data-testid={`tp-objective-${pi}-${gi}-${oi}`} value={obj.objective} onChange={(e) => patch((c) => { c.problems[pi].goals[gi].objectives[oi].objective = e.target.value; })} disabled={locked} /></div>
                            <div><FLabel>Interventions</FLabel><FInput data-testid={`tp-interventions-${pi}-${gi}-${oi}`} value={obj.interventions} onChange={(e) => patch((c) => { c.problems[pi].goals[gi].objectives[oi].interventions = e.target.value; })} disabled={locked} /></div>
                            <div><FLabel>Frequency</FLabel>
                              <FSelect value={obj.frequency} onChange={(e) => patch((c) => { c.problems[pi].goals[gi].objectives[oi].frequency = e.target.value; })} disabled={locked}>
                                {FREQUENCIES.map((f) => <option key={f}>{f}</option>)}
                              </FSelect>
                            </div>
                            {!locked && goal.objectives.length > 1 && (
                              <button type="button" aria-label="Remove objective" onClick={() => patch((c) => c.problems[pi].goals[gi].objectives.splice(oi, 1))}
                                className="h-9 w-9 rounded-full bg-white text-[#7B6157] flex items-center justify-center hover:text-[#C62828]"><Trash2 size={13} /></button>
                            )}
                          </div>
                        ))}
                        {!locked && (
                          <button type="button" data-testid={`tp-add-objective-${pi}-${gi}`} onClick={() => patch((c) => c.problems[pi].goals[gi].objectives.push({ ...EMPTY_OBJECTIVE }))}
                            className="w-full rounded-lg border border-dashed border-[#D7CCC8] py-2 text-xs font-semibold text-[#7B6157] hover:bg-[#F5F2EB]">+ Add Objective</button>
                        )}
                      </div>
                    </div>
                  ))}
                  {!locked && (
                    <button type="button" data-testid={`tp-add-goal-${pi}`} onClick={() => patch((c) => c.problems[pi].goals.push(JSON.parse(JSON.stringify(EMPTY_GOAL))))}
                      className="w-full rounded-xl border border-dashed border-[#D7CCC8] py-2.5 text-sm font-semibold text-[#7B6157] hover:bg-[#F5F2EB]">+ Add Goal</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {(plan?.clinician_signature || plan?.client_signature) && (
          <div className="flex flex-wrap gap-4">
            <SignatureDisplay sig={plan.clinician_signature} label="Clinician Signature" />
            <SignatureDisplay sig={plan.client_signature} label="Client Signature" />
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-3 pt-2 border-t border-[#F1EEE7]">
          {!locked && <Btn data-testid="tp-save-btn" variant="outline" onClick={save} disabled={busy} title={`Save (${SAVE_HINT})`} aria-keyshortcuts="Control+S">{busy ? "Saving…" : "Save Plan"}</Btn>}
          {!isNew && !plan?.client_signature && (
            <Btn data-testid="tp-client-sign-btn" variant="outline" onClick={() => setSignRole("client")} disabled={busy}><PenLine size={14} /> Client Sign</Btn>
          )}
          {!isNew && !locked && (
            <Btn data-testid="tp-clinician-sign-btn" onClick={() => setSignRole("clinician")} disabled={busy}><PenLine size={14} /> Clinician Sign & Lock</Btn>
          )}
        </div>
      </Card>

      {!isNew && <GoldenThreadPanel planId={id} />}

      <SignModal open={!!signRole} onClose={() => setSignRole(null)} onSign={sign} busy={busy}
        title={signRole === "clinician" ? "Clinician Signature" : "Client Signature"}
        defaultName={signRole === "clinician" ? `${user?.first_name || ""} ${user?.last_name || ""}`.trim() : plan?.client_name || ""} />
    </div>
  );
}
